import { createRouter, createWebHistory, START_LOCATION } from 'vue-router'
import qs from 'qs'
import { isEqual } from 'lodash'
import {
  ensureLocalsLoaded,
  ensureSession,
  ensureSignedOut,
  ensureSignedIn,
  initialScrollWithHash,
  initializeBeacon,
  loadDiscount,
  focusMainContent,
  reloadOnStaleChunk
  // metaSocialLoad
} from './hooks'

// The history is injectable so tests can pass `createMemoryHistory()`. The web
// history reaches for `window.history`, and under jest the router's initial
// navigation can outlive the test — jsdom is torn down first, and the pending
// navigation then fails with `history is not defined`. A memory history has no
// such dependency.
//
// The old first parameter, `vueInstance`, is gone: every guard factory ignored
// it, and the one that did use it was reading `nextTick` off it, which is not a
// method on a Vue 3 app object.
export default (routes = [], history = createWebHistory()) => {
  const router = createRouter({
    // Router 4 takes a history implementation rather than a `mode` string.
    history,
    routes,

    parseQuery(query) {
      return qs.parse(query)
    },

    // Router 3 expected this to include the leading `?`; Router 4 adds it
    // itself, so returning one produced `/search??q=coffee` on every
    // query-carrying URL. Reloading such a URL then parsed the key as `?q`,
    // which is how search lost its query.
    stringifyQuery(query) {
      return qs.stringify(query)
    },

    scrollBehavior(to, from, savedPosition) {
      // allowing per-route scrollBehavior
      if (to.meta.scrollBehavior) {
        return to.meta.scrollBehavior(to, from, savedPosition)
      }

      // smooth scroll for same-path hash nav
      const isSamePathHash = to.hash && to.path === from.path
      const isRootWithHash = to.path.endsWith('/') && to.hash
      if (isSamePathHash || isRootWithHash) {
        // Router 4 scrolls to an element only under `el`; Router 3's `selector`
        // is passed straight to window.scrollTo, which then has no position and
        // does not move — every in-page link updated the URL and stayed put.
        return {
          el: to.hash,
          behavior: 'smooth'
        }
      }

      // back/forward nav
      if (savedPosition) {
        return { ...savedPosition, behavior: 'auto' }
      }

      // A first load with a hash is initialScrollWithHash's job: it waits for
      // late content and retries. Router 3 never ran scrollBehavior on the first
      // load; Router 4 does, and scrolling to the top here ran after that hook
      // and undid it, so /license#enforcement opened at the top of the page.
      if (from === START_LOCATION && to.hash) {
        return false
      }

      // query-only change → no scroll
      const onlyQueryChanged = to.path === from.path && to.hash === from.hash && !isEqual(to.query, from.query)
      if (onlyQueryChanged) {
        return false
      }

      // path or hash change → scroll to top
      if (to.path !== from.path || to.hash !== from.hash) {
        return { left: 0, top: 0, behavior: 'auto' }
      }

      return false
    }
  })

  router.beforeEach(ensureLocalsLoaded())
  router.beforeEach(ensureSession())
  router.beforeEach(ensureSignedOut())
  router.beforeEach(ensureSignedIn())
  router.beforeEach(loadDiscount())
  // router.beforeEach(metaSocialLoad())
  router.afterEach(initialScrollWithHash())
  router.afterEach(initializeBeacon())
  router.afterEach(focusMainContent())
  router.onError(reloadOnStaleChunk())

  return router
}
